/**
 * TabBar — Masters-style segmented pill, redesigned 2026-05-04.
 *
 * Brand reference: rounded-full hairline pill (white background), filled
 * active segment in deep accent green with white text, inactive segments
 * are plain green text. No icons — text labels carry the meaning.
 * Crisper, calmer than the previous icon+text glassmorphic chip cluster.
 */

interface TabBarProps {
  currentPage: string
  mode: 'consumer' | 'venue'
  onNavigate: (hash: string) => void
  hidden?: boolean
}

interface TabDef {
  id: string
  label: string
  hash: string
}

const CONSUMER_TABS: readonly TabDef[] = [
  { id: 'home',   label: 'Report', hash: '#/'        },
  { id: 'status', label: 'Status', hash: '#/status'  },
] as const

const VENUE_TABS: readonly TabDef[] = [
  { id: 'venue-log',       label: 'Log Found', hash: '#/venue'           },
  { id: 'venue-inventory', label: 'Inventory', hash: '#/venue/inventory' },
  { id: 'venue-dashboard', label: 'Dashboard', hash: '#/venue/dashboard' },
] as const

export default function TabBar({ currentPage, mode, onNavigate, hidden }: TabBarProps) {
  const tabs = mode === 'venue' ? VENUE_TABS : CONSUMER_TABS
  return (
    <div
      className={`
        shrink-0 flex justify-center px-4
        transition-all duration-250 ease-out
        ${hidden ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}
      `}
      style={{ paddingBottom: 'max(14px, env(safe-area-inset-bottom, 14px))' }}
    >
      <nav
        className="
          inline-flex items-center gap-1 p-1
          rounded-full
          border
        "
        style={{
          background: 'var(--surface-0)',
          borderColor: 'var(--border-default)',
        }}
        aria-label="Primary navigation"
      >
        {tabs.map((tab) => {
          const active = currentPage === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => onNavigate(tab.hash)}
              className="px-5 py-2 rounded-full transition-colors duration-150"
              style={
                active
                  ? {
                      // Lighter wash so the navigation pill recedes
                      // behind the page's primary action (the dark
                      // tap-to-talk button on the hero).
                      background: 'rgba(0, 103, 71, 0.55)',
                      color: 'var(--text-on-accent)',
                    }
                  : {
                      background: 'transparent',
                      color: 'var(--accent)',
                    }
              }
              aria-current={active ? 'page' : undefined}
            >
              <span
                className="text-[13px] tracking-wide"
                style={{ fontWeight: active ? 600 : 500 }}
              >
                {tab.label}
              </span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}
